import { call, debounce, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as ScenarioActions from 'app/actions/scenarios/scenarioAction.ts';
import { GuiStateActions } from 'app/actions/guiState/guiStateAction.ts';
import type { Action, ActionMeta } from 'redux-actions';
import type {
  AddScenarioCommand,
  BlockDto,
  CopyScenarioCommand,
  ScenarioDto,
  SimpleScenarioDto,
  RestoreScenarioVersionCommand,
  UpdateScenarioSettingsCommand
} from '@/api-gen/api.schemas';
import { BlockType, QualityLevel, VoiceResponseUsecase } from '@/api-gen/api.schemas';
import type { AxiosResponse } from 'axios';
import {
  scenariosFilterScenarios,
  scenariosGetScenario,
  scenariosGetScenarioVersion,
  scenariosAddScenario,
  scenariosEditScenarioInfo,
  scenariosEditScenarioContent,
  scenariosRestoreScenarioVersion,
  scenariosCopyScenario,
  scenariosRemoveScenario
} from '@/api-gen/api.ts';
import type { CustomChart } from 'app/models/scenarios/customChart.ts';
import { ScenarioChartStoreActions } from 'app/actions/scenarios/scenarioChartStoreAction.ts';
import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import * as IntentClipboardSelectors from 'app/selectors/intentClipboardSelectors.ts';
import * as ScenarioChartSelectors from 'app/selectors/scenarioChartSelectors.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import type ChartDOMPosition from '../../../models/guiState/chartDomPosition.ts';
import customHistory from '../../../../core/customHistory.ts';
import AppRoutes from '../../../utils/routes.ts';
import { ChartMapper, SceneMapper } from '../../../utils/flowChart/mapping/index.ts';
import { getCenterCoordinates, getNodeDecartPosition } from '../../../utils/flowChart/positionHelpers.ts';
import { IntentEditingActions } from 'app/actions/scenarios/intentEditingAction.ts';
import { ScenarioChartEditingActions } from 'app/actions/scenarios/scenarioChartEditingActions.ts';
import { IntentActions } from 'app/actions/scenarios/intentAction.ts';
import type { IIntentClipboardState } from 'app/reducers/scenarios/intentClipboard/index.ts';

import type { LinkedScenario } from 'app/models/intents/intentNodeProperties.ts';
import type { UserSaysPhrases, UserSaysPhrasesRequest } from 'app/models/scenarios/scenario.ts';

import type { Unwrap } from 'app/types/UtilityType.ts';
import logger, { getErrorForLog, getSafeErrorLog } from 'app/utils/logger.ts';
import { mapEvaluationProtocolElements, mapScenarioPersonas } from 'app/utils/scenarioSettingsMapper.ts';
import { addError } from 'app/actions/snackbar/snackbar.ts';
import { getApiErrorMessageFromData } from 'app/utils/apiErrorMessages.ts';

const createIntentPhrases = () => [
  {
    id: 0,
    text: ''
  }
];

const createDefaultAiResponses = () => [
  {
    text: '',
    voiceResponseUsecase: VoiceResponseUsecase.Success
  },
  {
    text: '',
    voiceResponseUsecase: VoiceResponseUsecase.Fail
  }
];

const createDefaultBlock = (name: string, type: BlockType, position: { x: number; y: number }): BlockDto => ({
  id: 0,
  name,
  intentPhrases: createIntentPhrases(),
  aiResponses: createDefaultAiResponses(),
  qualityLevel: QualityLevel.Good,
  type,
  posX: position.x,
  posY: position.y
});

export const createDefaultSceneBlocks = (): BlockDto[] => {
  const startBlockOffset = getNodeDecartPosition({ x: 0, y: 0 });
  const endBlockOffset = getNodeDecartPosition({ x: 500, y: 0 });

  return [
    createDefaultBlock('Start intent', BlockType.Start, startBlockOffset),
    createDefaultBlock('End intent', BlockType.End, endBlockOffset)
  ];
};

export const buildAddScenarioCommand = (
  payload: AddScenarioCommand,
  chartDomWidthAndHeight?: ChartDOMPosition | null
): AddScenarioCommand => {
  const chartSize = {
    x: chartDomWidthAndHeight?.chartDOMWidth ?? 0,
    y: chartDomWidthAndHeight?.chartDOMHeight ?? 0
  };

  const defaultCenterOffset = getCenterCoordinates({ x: 0, y: 0 }, chartSize, 1);
  const hasBlocks = payload.blocks != null && payload.blocks.length > 0;

  return {
    ...payload,
    blocks: hasBlocks ? payload.blocks : createDefaultSceneBlocks(),
    blockLinks: payload.blockLinks ?? [],
    centerX: payload.centerX ?? defaultCenterOffset.x,
    centerY: payload.centerY ?? defaultCenterOffset.y,
    scale: payload.scale ?? 1
  };
};

function* getScenarios() {
  yield put(ScenarioActions.setScenariosLoading(true));
  try {
    const response: AxiosResponse<SimpleScenarioDto[]> = yield call(scenariosFilterScenarios);

    // Merge new list data with existing scenarios to preserve detailed fields (context, blocks, etc.)
    // that were loaded by getDetailedScenario. SimpleScenarioDto omits these fields, so a plain
    // replacement would wipe them and cause the editor page to show a permanent loading state.
    const existingScenarios: ScenarioDto[] = yield select(ScenarioSelectors.scenariosSelector);
    const existingById = new Map(existingScenarios.map((s) => [s.id, s]));
    const merged = response.data.map((newScenario) => {
      const existing = existingById.get(newScenario.id);
      return existing ? { ...existing, ...newScenario } : newScenario;
    });

    yield put(ScenarioActions.setScenarios(merged as any));
  } catch (error) {
    logger.error(getErrorForLog(error, 'Failed to load scenarios'), { context: 'Failed to load scenarios', error: getSafeErrorLog(error) });
  } finally {
    yield put(ScenarioActions.setScenariosLoading(false));
  }
}

function* getDetailedScenario(params: Action<ScenarioActions.GetDetailedScenarioPayload>) {
  yield put(ScenarioActions.setIsScenarioOpening(true));
  yield put(ScenarioActions.setDetailedScenarioLoading(true));
  const scenarios: ScenarioDto[] = yield select(ScenarioSelectors.scenariosSelector);
  try {
    // If versionId is not provided, we should get the latest version
    const response: AxiosResponse<ScenarioDto> = yield call(scenariosGetScenario, params.payload.sceneId);

    yield put(ScenarioActions.setIsStreamingEnabledForScenario(response.data.useStreaming));

    const chartDOMInfo: ChartDOMPosition = {
      chartDOMHeight: params.payload.chartDOMHeight,
      chartDOMWidth: params.payload.chartDOMWidth
    };

    yield put(GuiStateActions.setChartParams(chartDOMInfo));

    const chart = ChartMapper.chartFromSceneFull(response.data as any, chartDOMInfo, scenarios as any);

    yield put(ScenarioChartStoreActions.initChart(chart));

    // check if there's a clipboard to insert
    const clipboard: IIntentClipboardState = yield select(IntentClipboardSelectors.intentClipboard);
    if (!!clipboard.intentClipboard && clipboard.clipboardTargetId === params.payload.sceneId) {
      yield put(
        IntentActions.setIntentClipboard({
          clipboardTargetId: undefined,
          intentClipboard: undefined
        })
      );

      yield put(IntentActions.addIntent(clipboard.intentClipboard));
    }
    const updatedScenarios = scenarios.map((s) => {
      if (s.id === response.data.id) {
        return {
          ...s,
          ...response.data
        };
      }
      return s;
    });
    yield put(ScenarioActions.setScenarios(updatedScenarios as any));
  } catch (error) {
    logger.error(getErrorForLog(error, 'Failed to load scenario details'), { context: 'Failed to load scenario details', error: getSafeErrorLog(error) });
  } finally {
    yield put(ScenarioActions.setDetailedScenarioLoading(false));
    yield put(ScenarioActions.setIsScenarioOpening(false));
  }
}

function* getDetailedScenarioDataByScenarioId(action: Action<UserSaysPhrasesRequest>) {
  try {
    if (action.payload) {
      const { sceneId, versionId, startblockId } = action.payload;

      const response: AxiosResponse<ScenarioDto> = versionId
        ? yield call(scenariosGetScenarioVersion, sceneId, versionId)
        : yield call(scenariosGetScenario, sceneId);

      if (response.data) {

        const startBlock = response.data.blocks?.find((block) => block.id == startblockId);

        if (startBlock) {
          let userSaysPhrases: UserSaysPhrases | null = null;

          userSaysPhrases = {
            scenarioId: sceneId,
            userSaysPhrases: startBlock.intentPhrases?.map((p) => p.text) ?? []
          };

          yield put(ScenarioActions.setDetailedScenarioDataByScenarioId(userSaysPhrases));
        }
      }
    }
  } catch (error) {
    logger.error(getErrorForLog(error, 'Failed to load scenario data by scenario id'), { context: 'Failed to load scenario data by scenario id', error: getSafeErrorLog(error) });
  }
}

function* saveDetailedScenario() {
  yield put(ScenarioActions.setDetailedScenarioSaving(true));
  try {
    const currentChart: CustomChart = yield select(ScenarioChartSelectors.scenarioChart);
    const chartDOMInfo: ChartDOMPosition = yield select(GuiStateSelectors.chartPositionSelector);
    const scenarioDetails: ScenarioDto = yield select(ScenarioSelectors.getScenarioById(currentChart.properties.id));
    const scenarios: Array<ScenarioDto> = yield select(ScenarioSelectors.scenariosSelector);
    const scene = SceneMapper.sceneFromChartAndScenario(currentChart, chartDOMInfo, scenarioDetails as any, scenarios as any);
    // scene.companyId = scenarioDetails.companyId;

    const response: AxiosResponse<ScenarioDto> = yield call(() => scenariosEditScenarioContent(scene as any));
    yield put(ScenarioActions.setIsStreamingEnabledForScenario(response.data.useStreaming));
    yield put(ScenarioActions.setScenarioHasNotBeenModified(true));
    yield put(ScenarioActions.setIsCurrentScenarioUpdate(true));

    const chart = ChartMapper.chartFromSceneFull(response.data as any, chartDOMInfo, scenarios as any);

    const updatedScenarios = scenarios.map((s) => {
      if (s.id === response.data.id) {
        return {
          ...s,
          ...response.data
        };
      }
      return s;
    });
    yield put(ScenarioActions.setScenarios(updatedScenarios as any));

    yield put(ScenarioChartStoreActions.initChart(chart));

    yield put(ScenarioActions.setCurrentScenarioSaveStatus(true));
  } catch (error) {
    logger.error(getErrorForLog(error, 'Failed to save scenario'), { context: 'Failed to save scenario', error: getSafeErrorLog(error) });
  } finally {
    yield put(ScenarioActions.setDetailedScenarioSaving(false));
  }
}

function* restoreScenario(params: Action<{ scenarioId: number; versionId: number }>) {
  yield put(ScenarioActions.setDetailedScenarioLoading(true));
  try {
    const chartDOMInfo: ChartDOMPosition = yield select(GuiStateSelectors.chartPositionSelector);
    const scenarios: Array<ScenarioDto> = yield select(ScenarioSelectors.scenariosSelector);

    const restoreScenarioCommand: RestoreScenarioVersionCommand = {
      scenarioId: params.payload.scenarioId,
      versionId: params.payload.versionId
    };

    const response: AxiosResponse<ScenarioDto> = yield call(() =>
      scenariosRestoreScenarioVersion(restoreScenarioCommand as any)
    );
    yield put(ScenarioActions.setScenarioHasNotBeenModified(true));
    yield put(ScenarioActions.setIsCurrentScenarioUpdate(true));

    const chart = ChartMapper.chartFromSceneFull(response.data as any, chartDOMInfo, scenarios as any);

    const updatedScenarios = scenarios.map((s) => {
      if (s.id === response.data.id) {
        return {
          ...s,
          ...response.data
        };
      }
      return s;
    });
    yield put(ScenarioActions.setScenarios(updatedScenarios as any));

    yield put(ScenarioChartStoreActions.initChart(chart));

    yield put(ScenarioActions.setCurrentScenarioSaveStatus(true));
  } catch (error) {
    logger.error(getErrorForLog(error, 'Failed to restore scenario version'), { context: 'Failed to restore scenario version', error: getSafeErrorLog(error) });
  } finally {
    yield put(ScenarioActions.setDetailedScenarioLoading(false));
  }
}

function* addScenario(params: Action<AddScenarioCommand>) {
  logger.log(params.payload);
  const chartDomWidthAndHeight: ChartDOMPosition | null = yield select(
    GuiStateSelectors.chartPositionSelector
  );
  const addScene: AddScenarioCommand = buildAddScenarioCommand(params.payload, chartDomWidthAndHeight);

  yield put(ScenarioActions.setDetailedScenarioSaving(true));
  try {
    const response: AxiosResponse<ScenarioDto> = yield call(() => scenariosAddScenario(addScene as any));

    const scenarios: ScenarioDto[] = yield select(ScenarioSelectors.scenariosSelector);

    // Construct a new scenario object using the real, server-supplied startBlockId
    const newScenario = {
      ...response.data,
      scenarioContextIntro: params.payload.scenarioContextIntro,
      playScenarioContextOn: params.payload.playScenarioContextOn
    };

    yield put(
      ScenarioActions.setScenarios([...scenarios, newScenario])
    );

    // Mark the newly created scenario as current, so ScenarioDisplay will load it immediately
    yield put(ScenarioActions.setCurrentScenario({
      id: 0,
      sceneId: response.data.id,
      sceneName: response.data.name,
      isScenarioSaved: true,
      startBlockId: response.data.startBlockId ?? 0,
      companyId: response.data.companyId,
      versionId: response.data.versionId
    }));

    // redirect to the new scenario
    customHistory.push(`${AppRoutes.Scene}/${response.data.id}`);
  } catch (error: any) {
    logger.error(getErrorForLog(error, 'Failed to create scenario'), { context: 'Failed to create scenario', error: getSafeErrorLog(error) });
    const errorText = getApiErrorMessageFromData(error?.response?.data, 'Failed to create scenario.');
    yield put(addError({ errorText }));
  } finally {
    yield put(ScenarioActions.setDetailedScenarioSaving(false));
  }
}

function* updateScenarioDetails(params: Action<UpdateScenarioSettingsCommand>) {

  yield put(ScenarioActions.setIsStreamingEnabledForScenario(params.payload.useStreaming));
  const editSceneInfo: UpdateScenarioSettingsCommand = params.payload;

  yield put(ScenarioActions.setDetailedScenarioSaving(true));
  try {
    const response: AxiosResponse<ScenarioDto> = yield call(() => scenariosEditScenarioInfo(editSceneInfo as any));

    const scenarios: ScenarioDto[] = yield select(ScenarioSelectors.scenariosSelector);

    const updatedScenarios = scenarios.map((s) => {
      if (s.id === response.data.id) {
        return {
          ...s,
          ...response.data,
          scenarioContextIntro: params.payload.scenarioContextIntro,
          playScenarioContextOn: params.payload.playScenarioContextOn
        };
      }
      return s;
    });

    yield put(ScenarioActions.setScenarios(updatedScenarios as any));
  } catch (error: any) {
    logger.error(getErrorForLog(error, 'Failed to save scenario settings'), { context: 'Failed to save scenario settings', error: getSafeErrorLog(error) });
    const errorText = getApiErrorMessageFromData(error?.response?.data, 'Failed to save scenario settings.');
    yield put(addError({ errorText }));
  } finally {
    yield put(ScenarioActions.setDetailedScenarioSaving(false));
  }
}

function* copyScenario(params: Action<CopyScenarioCommand>) {
  const copyScene: CopyScenarioCommand = params.payload;

  try {
    yield call(() => scenariosCopyScenario(copyScene as any));
    // Background job initiated - user will be notified via SignalR when copy is complete
  } catch (error) { }
}
function* updateScenarioChart() {
  yield put(ScenarioActions.setScenarioHasNotBeenModified(false));
  yield put(ScenarioActions.setIsCurrentScenarioUpdate(false));
  yield put(ScenarioActions.setCurrentScenarioSaveStatus(false));
}

function* setLinks(params: Action<number>) {
  const nodes = yield select(ScenarioChartSelectors.chartNodes);
  const currentScenario: LinkedScenario | undefined = yield select(ScenarioSelectors.currentScenario);
  const selectedScenario: ScenarioDto | undefined = yield select(ScenarioSelectors.getScenarioById(params.payload));

  let temp: LinkedScenario[] = [];

  if (nodes) {
    const allLinks = Object.values(nodes)
      .filter((x: any) => x.properties.linkedScenarios != undefined)
      .map((x: any) => x.properties.linkedScenarios);
    temp = allLinks.reduce((accumulator, currentValue) => [...accumulator, ...currentValue], []);
  }

  if (currentScenario) {
    temp.unshift({
      id: 0,
      sceneId: currentScenario.sceneId,
      sceneName: currentScenario.sceneName ?? '',
      startBlockId: currentScenario.startBlockId ?? 0
    });
  } else if (selectedScenario) {
    temp.unshift({
      id: 0,
      sceneId: selectedScenario.id,
      sceneName: selectedScenario.name ?? '',
      startBlockId: selectedScenario.startBlockId ?? 0
    });
  } else {
    yield put(ScenarioActions.setLinkedtScenarios([]));
    yield put(ScenarioActions.setCurrentTabSelected(0));
    return;
  }

  yield put(ScenarioActions.setLinkedtScenarios(temp));
  yield put(ScenarioActions.setCurrentTabSelected(0));
}

function* goBackToParentScenario() {
  const parentScenario = yield select(ScenarioSelectors.parentScenario);

  if (parentScenario) {
    const temp = parentScenario;

    const lastElem = temp.pop();

    yield put(ScenarioActions.setParentScenario(temp.length == 0 ? undefined : temp));
    if (lastElem) {
      yield put(ScenarioActions.setCurrentScenario(lastElem));
    }
  }
}

function* initToolbar(params: Action<number>) {
  const selectedScenario: ScenarioDto | undefined = yield select(ScenarioSelectors.getScenarioById(params.payload));

  yield put(ScenarioActions.setParentScenario(undefined));

  if (!selectedScenario) {
    yield put(ScenarioActions.setRootScenario(undefined));
    yield put(ScenarioActions.setCurrentScenario(undefined));
    yield put(ScenarioActions.setLinkedtScenarios([]));
    return;
  }

  yield put(ScenarioActions.setRootScenario(selectedScenario as any));

  yield put(
    ScenarioActions.setCurrentScenario({
      id: 0,
      sceneId: selectedScenario.id,
      sceneName: selectedScenario.name ?? '',
      isScenarioSaved: true,
      startBlockId: selectedScenario.startBlockId ?? 0,
      companyId: selectedScenario.companyId,
      versionId: selectedScenario.versionId
    })
  );
}

function* switchCurrentScenarioTo(params: Action<number>) {
  yield put(ScenarioChartStoreActions.onShowIntentEdit(false));
  const links = yield select(ScenarioSelectors.linkedScenarios);
  const parentScenario = yield select(ScenarioSelectors.parentScenario);

  let temp = parentScenario;
  if (!Array.isArray(temp)) temp = [];

  temp.push(links[0]);

  yield put(ScenarioActions.setParentScenario(temp));
  yield put(ScenarioActions.setCurrentScenario(links[params.payload]));
}

function* deleteScenario(params: ActionMeta<{ id: number }, { navigationAction: () => void }>) {
  yield put(ScenarioActions.setDetailedScenarioLoading(true));
  const scenarios: ScenarioDto[] = yield select(ScenarioSelectors.scenariosSelector);

  try {
    yield call(() => scenariosRemoveScenario(params.payload.id as any));
    params.meta.navigationAction();
    yield put(ScenarioActions.setCurrentScenario(undefined));
    yield put(ScenarioActions.setScenarios(scenarios.filter((x) => x.id != params.payload.id)));
    yield getScenarios();
  } catch (error) {
    logger.error(getErrorForLog(error, 'Failed to delete scenario'), { context: 'Failed to delete scenario', error: getSafeErrorLog(error) });
  } finally {
    yield put(ScenarioActions.setDetailedScenarioLoading(false));
  }
}

function* watchSaveDetailedScenario() {
  yield takeLatest(ScenarioActions.Type.SAVE_DETAILED_SCENARIO, saveDetailedScenario);
}

function* watchGetDetailedScenario() {
  yield takeLatest(ScenarioActions.Type.GET_DETAILED_SCENARIO, getDetailedScenario);
}

function* watchGeDetailedScenariosDataByScenarioId() {
  yield takeEvery(ScenarioActions.Type.GET_DETAILED_SCENARIO_DATA_BY_SCENARIO_ID, getDetailedScenarioDataByScenarioId);
}

function* watchGetScenarios() {
  yield takeLatest(ScenarioActions.Type.GET_SCENARIOS, getScenarios);
}

function* watchAddScenario() {
  yield takeLatest(ScenarioActions.Type.ADD_SCENARIO, addScenario);
}

function* watchUpdateScenarioDetails() {
  yield takeLatest(ScenarioActions.Type.UPDATE_SCENARIO_DETAILS, updateScenarioDetails);
}

function* watchRestoreScenario() {
  yield takeLatest(ScenarioActions.Type.RESTORE_SCENARIO, restoreScenario);
}

function* watchCopyScenario() {
  yield takeLatest(ScenarioActions.Type.COPY_SCENARIO, copyScenario);
}

function* watchUpdateScenarioChartElements() {
  yield debounce(
    300,
    [
      ...Object.entries(IntentEditingActions.Type).map((pair) => Object.values(pair)[1]),
      ScenarioChartEditingActions.Type.ON_LINK_COMPLETE,
      ScenarioChartEditingActions.Type.ON_DELETE_SELECTED,
      ScenarioChartEditingActions.Type.ON_DRAG_NODE_STOP,
      ScenarioChartEditingActions.Type.ON_DELETE_ALL_LINKS,
      IntentActions.Type.ADD_INTENT,
      IntentActions.Type.REMOVE_INTENT
    ],
    updateScenarioChart
  );
}

function* watchLinks() {
  yield takeLatest(ScenarioActions.Type.SET_LINKS, setLinks);
}

function* watchGoBack() {
  yield takeLatest(ScenarioActions.Type.GO_BACK_TO_PARENT_SCENARIO, goBackToParentScenario);
}

function* watchInitToolBar() {
  yield takeLatest(ScenarioActions.Type.INIT_TOOLBAR, initToolbar);
}

function* watchOnTabClick() {
  yield takeLatest(ScenarioActions.Type.SWITCH_CURRENT_SCENARIO_TO, switchCurrentScenarioTo);
}

function* watchDeleteScenario() {
  yield takeLatest(ScenarioActions.Type.DELETE_SCENARIO, deleteScenario);
}

function* updateScenarioVariablesPartial(action: Action<{ scenarioId: number; variables: any[] }>) {
  try {
    const { scenarioId, variables } = action.payload;

    // Get the current scenario for other properties
    const scenarioDetails: ScenarioDto = yield select(ScenarioSelectors.getScenarioById(scenarioId));

    if (!scenarioDetails) {
      logger.error('Could not find scenario with ID:', scenarioId);
      return;
    }

    // Create the partial update command (only update the variables field)
    const editSceneInfo: UpdateScenarioSettingsCommand = {
      ...scenarioDetails,
      evaluationProtocolElements: mapEvaluationProtocolElements(scenarioDetails.evaluationProtocolElements),
      notesEvaluationProtocolElements: mapEvaluationProtocolElements(scenarioDetails.notesEvaluationProtocolElements),
      scenarioPersonas: mapScenarioPersonas(scenarioDetails.scenarioPersonas),
      variables
    };

    // Call the API - use the same endpoint as updateScenarioDetails
    // but this happens separately and doesn't trigger the full scenario reload
    const response: AxiosResponse<ScenarioDto> = yield call(() => scenariosEditScenarioInfo(editSceneInfo as any));

    // No additional actions here - the reducer has already updated the local state
    // This is only to sync with the server
  } catch (error) {
    logger.error(getErrorForLog(error, 'Error updating scenario variables partially'), { context: 'Error updating scenario variables partially', error: getSafeErrorLog(error) });
  }
}

function* watchUpdateScenarioVariablesPartial() {
  yield takeLatest(ScenarioActions.Type.UPDATE_SCENARIO_VARIABLES_PARTIAL, updateScenarioVariablesPartial);
}

export { addScenario };

export default function* watchAllScenarioSagas() {
  yield fork(watchSaveDetailedScenario);
  yield fork(watchRestoreScenario);
  yield fork(watchGetDetailedScenario);
  yield fork(watchGeDetailedScenariosDataByScenarioId);
  yield fork(watchGetScenarios);
  yield fork(watchUpdateScenarioChartElements);
  yield fork(watchAddScenario);
  yield fork(watchUpdateScenarioDetails);
  yield fork(watchUpdateScenarioVariablesPartial); // Add new watcher
  yield fork(watchCopyScenario);
  yield fork(watchLinks);
  yield fork(watchDeleteScenario);
  yield fork(watchGoBack);
  yield fork(watchInitToolBar);
  yield fork(watchOnTabClick);
}
